import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  User, 
  BarChart3, 
  Calendar, 
  Users, 
  UserCheck, 
  Megaphone, 
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import NotificationBell from '@/components/NotificationBell';
import ThemeToggle from '@/components/ThemeToggle';

const Sidebar = ({ user, onLogout, isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isPartner = user?.role === 'parceiro';

  const handleLogout = () => {
    onLogout();
    navigate('/auth');
  };

  // Menu items baseado no role do usuário
  const menuItems = isAdmin ? [
    { icon: Users, label: 'Clientes', path: '/clientes' },
    { icon: UserCheck, label: 'Parceiros', path: '/parceiros' },
    { icon: Calendar, label: 'Períodos', path: '/periodos' },
    { icon: Megaphone, label: 'Comunicados', path: '/comunicados' },
    { icon: BarChart3, label: 'Relatórios', path: '/relatorios' },
    { icon: History, label: 'Auditoria', path: '/auditoria' },
    { icon: User, label: 'Perfil', path: '/perfil' },
  ] : [
    { icon: Users, label: 'Meus Clientes', path: '/clientes' },
    { icon: Megaphone, label: 'Comunicados', path: '/comunicados' },
    { icon: User, label: 'Perfil', path: '/perfil' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full bg-card border-r border-border transition-all duration-300 flex flex-col",
          isOpen ? "w-64" : "w-20",
          "lg:translate-x-0",
          !isOpen && "max-lg:-translate-x-full"
        )}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {isOpen ? (
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="SUA Gestão" className="h-8 w-8 object-contain" />
              <span className="font-bold text-lg text-primary">SUA Gestão</span>
            </div>
          ) : (
            <img src="/logo.png" alt="SUA Gestão" className="h-8 w-8 object-contain mx-auto" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(!isOpen)}
            className="hidden lg:flex"
          >
            {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        {/* User info */}
        {isOpen && (
          <div className="px-4 py-3 border-b border-border overflow-hidden">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" title={user?.name}>{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role === 'admin' ? 'Administrador' : 'Parceiro'}</p>
              </div>
              {isPartner && <NotificationBell />}
            </div>
          </div>
        )}
        
        {/* Notification bell when sidebar is collapsed (partner only) */}
        {!isOpen && isPartner && (
          <div className="flex justify-center py-2 border-b border-border">
            <NotificationBell />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => window.innerWidth < 1024 && setIsOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  "hover:bg-primary/10 hover:text-primary",
                  isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground",
                  !isOpen && "justify-center"
                )
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {isOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer - Theme Toggle & Logout */}
        <div className="p-4 border-t border-border space-y-2">
          <ThemeToggle collapsed={!isOpen} />
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className={cn(
              "w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10",
              !isOpen && "px-0 justify-center"
            )}
          >
            <LogOut className="h-5 w-5" />
            {isOpen && <span className="ml-2">Sair</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile menu button */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => setIsOpen(true)}
        className="fixed top-4 left-4 z-30 lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </Button>
    </>
  );
};

export default Sidebar;
